import { createFileRoute, notFound } from "@tanstack/react-router";

import { NotFound } from "@/components/not-found";
import { PublicItemDetailPage } from "@/components/catalog/public-item-detail-page";
import { usePublicCatalogItem } from "@/hooks/catalog-public-item";
import { itemDetailIsLoading } from "@/lib/catalog-detail-loading";

// **The anonymous product page** — `/products/:assetId`, the donor's own URL.
//
// A top-level route, deliberately outside `_authed/`: this is the one page in
// the app a stranger is meant to reach, and it is reached from links that have
// been in the wild for years (`App.tsx:84` mounts it under `PublicRoute`, a
// sibling of login and logout and outside `PublicLayout`, so no header or nav
// renders). Its directory is named in the routes-layout convention test's
// `PUBLIC_ENTRIES`, and the entry inside it in `PUBLIC_DIRECTORY_ENTRIES` — a
// param segment is the last place a page should arrive publicly by accident.
//
// The maintenance takeover still paints over it: that sits around the root
// `Outlet` (`__root.tsx`), which every route including this one renders through.
//
// **`assetId`, not `itemId`.** The donor's param name, kept because the URLs are
// the reason this page exists — and the value is the modsy primary key, which
// ADR-0004 preserves through the migration precisely so those links keep
// resolving.

/**
 * The item id a path segment names, or `null` if it names none.
 *
 * The donor writes `Number(assetId)` and skips its query on the falsy result
 * (`item-details-page.tsx`; `ItemDetailsIsland.tsx:125`), so a mistyped link
 * costs modsy no request and leaves the visitor on an empty shell forever.
 * Atlas keeps the first half exactly and replaces the second with the branded
 * not-found page — the divergence spec #270 records.
 *
 * **`Number`'s own coercion, not a stricter one.** An earlier cut here matched
 * `/^\d+$/` and so answered `null` for `0x10`, `1e3`, `+5` and `5.0` — all of
 * which modsy coerces and then *renders the item for* (`0x10` is item 16
 * there). Narrowing them to the not-found page reads like an improvement and is
 * one, which is exactly why it is not this port's to make: these are URLs the
 * source resolves, so the port resolves them too. `Number` also reads a padded
 * `02391` as the item it plainly is, which the regex got right by accident.
 *
 * The guard that remains is not a narrowing of the donor but the other half of
 * its own behaviour: `Number` yields `0` for `""` and `" "`, `NaN` for `abc`,
 * `1.5` for `"1.5"` and `Infinity` for `"9e99"`, and modsy either skips its
 * query on the falsy ones or asks falcon for an id it cannot have and gets
 * nothing back. Both of those are the empty shell, which is the state spec #270
 * rules into the branded not-found page.
 *
 * Exported because it is the whole of the "no wire call" behaviour and is worth
 * asserting without standing up a router.
 */
export function parseAssetId(raw: string): number | null {
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * Whether the read has come back and said the item is not there.
 *
 * The other half of this route's not-found story: `parseAssetId` above catches
 * an id that cannot name an item, and this catches an id that could but does
 * not. The AC lists them separately — "a non-numeric path param short-circuits
 * to not-found without a wire call; a null result from the read converts to the
 * router-level not-found component".
 *
 * **`isSuccess`, not `!data`.** A read that *succeeded* on a missing item is a
 * dead link, and the transport is what says so: the declaration is `nullOn` the
 * not-found tag precisely so a missing item renders a page instead of throwing.
 * A read that *failed* is a different thing — it keeps the donor's snackbar over
 * the page below — and a read still in flight has no verdict yet. Asking
 * `!query.data` would collapse all three into the branded 404.
 *
 * Exported for the same reason `parseAssetId` is: it is the whole of a stated
 * acceptance path, and the throw it guards happens during a component render,
 * which `router.load()` in the routed tests cannot reach.
 */
export function isDeadItemLink(query: { isSuccess: boolean; data: unknown }): boolean {
  return query.isSuccess && (query.data ?? null) === null;
}

export const Route = createFileRoute("/products/$assetId")({
  // **No title, and noindex.** The donor's tab title is static and app-wide
  // (`timmy/index.html:5`) and its `robots` meta sits in the same file (`:10`),
  // so neither is per-item there and neither becomes per-item here. The two go
  // together: a per-item title is exactly the string a search result would
  // print, and this page is at parity un-indexable. No OG tags either — nothing
  // for a link preview to unfurl.
  head: () => ({ meta: [{ name: "robots", content: "noindex" }] }),
  /**
   * The id, or the branded not-found page — and **no read**.
   *
   * The donor writes `Number(assetId)` and skips its query on the falsy result
   * (`item-details-page.tsx`; `ItemDetailsIsland.tsx:125`), so a mistyped link
   * costs modsy no request. Raising here rather than in the component keeps
   * that true on the server too: the SSR pass renders the 404 itself, and no
   * wire call is made on either side.
   *
   * **It deliberately does not prefetch the item.** A loader prefetch on this
   * route is not free either way, and both ways cost a source-visible
   * behaviour:
   *
   *   - *Awaited*, the server holds the response until Python answers and the
   *     visitor's first paint is the finished page. The donor's loading state —
   *     three skeletons under a manufacturer line that already holds its height
   *     (`MetaLoadingSkeleton.tsx`, `ItemDetailsIsland.tsx:156-158`) — then has
   *     no way to render for an anonymous visitor at all, which is dropping a
   *     behaviour the source has, not implementing one differently.
   *   - *Un-awaited*, the read resolves after the HTML is rendered but before
   *     the query cache is dehydrated into the stream, so the browser hydrates
   *     an item into a tree the server painted as skeletons. React logs a
   *     hydration mismatch and throws the server's markup away — observed on
   *     this very page before this comment existed.
   *
   * So the read stays where the donor has it and where the *authed* details
   * route already keeps its own: in the component's hook, running on the
   * client. Nothing is lost by it — no link in this app points here and the
   * route is entered cold, so there is no navigation for a prefetch to be
   * early for.
   */
  loader: ({ params }) => {
    const itemId = parseAssetId(params.assetId);
    if (itemId === null) throw notFound();
    return { itemId };
  },
  // The router's `defaultNotFoundComponent` covers a `notFound()` raised from
  // a loader, which is the only place the rest of this app raises one. This
  // route raises a second one *during render* — the null-mapped read below —
  // and that one needs a boundary declared on the route itself, or it escapes
  // as a render error and the visitor gets the crash screen instead of the 404.
  notFoundComponent: NotFound,
  component: PublicItemRoute,
});

function PublicItemRoute() {
  const { itemId } = Route.useLoaderData();
  const query = usePublicCatalogItem(itemId);

  if (isDeadItemLink(query)) throw notFound();

  return (
    <PublicItemDetailPage
      item={query.data ?? null}
      isLoading={itemDetailIsLoading(query)}
      isError={query.isError}
    />
  );
}
